import dayjs from 'dayjs';
import {
  IPC_ENUM,
  milestoneStatusLabel,
  milestoneStatusOptions,
  resolveIpcEnumValue,
} from './ipcStatusEnums';

/** 节点进度：三态（编辑器）；terminated 在排期里单独展示 */
export type NodeProgressStatus = 'pending' | 'in_progress' | 'completed';

/** 节点时间维度：正常 / 延期（红环） */
export type NodeTimeStatus = 'normal' | 'delayed';

/** 动态取展示名（来自 erpa_base_tbl） */
export function getNodeProgressLabel(progress: NodeProgressStatus): string {
  return milestoneStatusLabel(progress);
}

/** @deprecated 使用 getNodeProgressLabel；保留 getter 兼容旧引用 */
export const NODE_PROGRESS_LABELS: Record<NodeProgressStatus, string> = {
  get pending() {
    return milestoneStatusLabel('pending');
  },
  get in_progress() {
    return milestoneStatusLabel('in_progress');
  },
  get completed() {
    return milestoneStatusLabel('completed');
  },
};

export function getNodeProgressOptions(): ReadonlyArray<{ value: NodeProgressStatus; label: string }> {
  const allowed = new Set<string>(['pending', 'in_progress', 'completed']);
  const fromBase = milestoneStatusOptions().filter(o => allowed.has(o.value));
  if (fromBase.length > 0) {
    return fromBase.map(o => ({
      value: o.value as NodeProgressStatus,
      label: o.label,
    }));
  }
  // 枚举未加载时仅返回英文 value，避免写死中文
  return [
    { value: 'pending', label: 'pending' },
    { value: 'in_progress', label: 'in_progress' },
    { value: 'completed', label: 'completed' },
  ];
}

/** @deprecated 使用 getNodeProgressOptions */
export const NODE_PROGRESS_OPTIONS: ReadonlyArray<{ value: NodeProgressStatus; label: string }> = [
  { value: 'pending', label: 'pending' },
  { value: 'in_progress', label: 'in_progress' },
  { value: 'completed', label: 'completed' },
];

/** 后端 / 历史字段 → 标准进度（英文码；中文名经 base 反查） */
export function normalizeNodeProgress(raw?: string | null): NodeProgressStatus {
  const s = (raw ?? '').trim().toLowerCase();
  if (!s || s === 'empty') return 'pending';
  if (s === 'pending' || s === 'planning') return 'pending';
  if (s === 'in_progress' || s === 'active') return 'in_progress';
  if (s === 'completed' || s === 'done') return 'completed';
  if (s === 'terminated') return 'pending';

  const resolved = resolveIpcEnumValue(IPC_ENUM.MILESTONE_STATUS, raw);
  if (resolved) {
    const r = resolved.toLowerCase();
    if (r === 'pending' || r === 'planning') return 'pending';
    if (r === 'in_progress') return 'in_progress';
    if (r === 'completed') return 'completed';
  }
  return 'pending';
}

/** 解析 API 状态字符串（含 legacy delay） */
export function parseNodeStatus(raw?: string | null): {
  progress: NodeProgressStatus;
  time: NodeTimeStatus;
} {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'delay' || s === 'delayed') {
    return { progress: 'in_progress', time: 'delayed' };
  }
  return { progress: normalizeNodeProgress(raw), time: 'normal' };
}

export type NodeTimeInput = {
  progress: NodeProgressStatus;
  planStartDate?: string | null;
  planEndDate?: string | null;
  /** 业务层已算好的延期标记（如汇报页 anomaly） */
  delayed?: boolean;
};

/** 根据计划日期判定是否延期（与项目汇报 calcAnomaly 规则一致） */
export function resolveNodeTimeStatus(input: NodeTimeInput): NodeTimeStatus {
  if (input.delayed) return 'delayed';

  const now = dayjs().startOf('day');
  const { progress, planStartDate, planEndDate } = input;

  const planStart = planStartDate ? dayjs(planStartDate).startOf('day') : null;
  const planEnd = planEndDate ? dayjs(planEndDate).startOf('day') : null;

  if (
    planStart?.isValid()
    && !planStart.isAfter(now)
    && progress === 'pending'
  ) {
    return 'delayed';
  }

  if (
    planEnd?.isValid()
    && planEnd.isBefore(now)
    && progress !== 'completed'
  ) {
    return 'delayed';
  }

  return 'normal';
}

export function nodeStatusTitle(progress: NodeProgressStatus, time: NodeTimeStatus): string {
  const label = getNodeProgressLabel(progress);
  return time === 'delayed' ? `${label} · 延期` : label;
}
